// frontend/src/pages/CategoryManager.js
// Finance categories used by the cashbook. Chrome/ink, French.
//
// Categories can sit in a group. One category per employee is the right level
// to record at — it answers "what did I pay Kouassi this year" — but on its own
// it cannot answer "what did I pay in wages in August", because nothing ties
// those categories together. The group is that tie, and the ledger totals by it.

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import {
  Page, Panel, Button, EmptyState, Loading, ErrorNote, inputClass,
} from '../components/ui/Iparc';

const UNGROUPED = '__none__';

// Offered as a starting point; the field is free text, so any bucket works.
const SUGGESTED = ['Salaires', 'Charges', 'Fournisseurs', 'Loyer', 'Taxes'];

const CategoryManager = () => {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [newName, setNewName] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null); // { id, group }
  const [error, setError] = useState('');

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/finance/categories`, auth);
      setCategories(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (err) {
      setError('Échec du chargement des catégories.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API_URL}/finance/categories`, { name, group: newGroup.trim() }, auth);
      setNewName('');
      // The group is deliberately kept: adding six employees in a row means
      // typing "Salaires" once, not six times.
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.error || "Échec de l'ajout de la catégorie.");
    } finally {
      setSaving(false);
    }
  };

  const assignGroup = async (id, group) => {
    setBusyId(id);
    setError('');
    try {
      await axios.patch(`${API_URL}/finance/categories/${id}`, { group }, auth);
      setEditing(null);
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Échec de la mise à jour.');
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    if (token) fetchCategories();
    // eslint-disable-next-line
  }, [token]);

  // Existing group names feed the pickers, so a typo cannot silently create a
  // second "Salaires " that never adds up with the first.
  const knownGroups = useMemo(() => {
    const found = new Set(SUGGESTED);
    categories.forEach((c) => { if (c.group) found.add(c.group); });
    return [...found].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [categories]);

  const grouped = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => {
      const key = c.group || UNGROUPED;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    return [...map.entries()]
      .sort((a, b) => {
        if (a[0] === UNGROUPED) return 1;
        if (b[0] === UNGROUPED) return -1;
        return a[0].localeCompare(b[0], 'fr');
      });
  }, [categories]);

  const ungroupedCount = categories.filter((c) => !c.group).length;

  return (
    <Page title="Catégories" subtitle="Gestion" width="narrow">
      <p className="text-[12.5px] text-[#8F877B] -mt-2 max-w-[600px]">
        Les catégories classent les écritures du grand livre. Un groupe les
        rassemble pour en obtenir un total : une catégorie par employé, toutes
        dans « Salaires », donne le détail par personne et le total du mois.
      </p>

      {error && <ErrorNote>{error}</ErrorNote>}

      <Panel title="Ajouter une catégorie">
        <form onSubmit={handleAdd} className="grid gap-2.5 sm:grid-cols-[1fr_1fr_auto] items-end">
          <div>
            <label className="block text-[11px] text-[#8F877B] mb-1.5" htmlFor="cat-name">Nom</label>
            <input
              id="cat-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Ex : Kouassi, loyer, pièces…"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[11px] text-[#8F877B] mb-1.5" htmlFor="cat-group">
              Groupe <span className="text-[#5C554C]">(optionnel)</span>
            </label>
            <input
              id="cat-group"
              value={newGroup}
              onChange={(e) => setNewGroup(e.target.value)}
              placeholder="Ex : Salaires"
              list="known-groups"
              className={inputClass}
            />
            <datalist id="known-groups">
              {knownGroups.map((g) => <option key={g} value={g} />)}
            </datalist>
          </div>
          <Button as="button" type="submit" variant="primary" className="h-[38px]"
            disabled={saving || !newName.trim()}>
            {saving ? 'Ajout…' : 'Ajouter'}
          </Button>
        </form>
      </Panel>

      {ungroupedCount > 0 && categories.length > ungroupedCount && (
        <div className="rounded-xl border border-[#2B2724] border-l-2 border-l-[#DFA23C] bg-[#141210] px-4 py-3">
          <p className="text-[12.5px] text-[#C9C3B6]">
            {ungroupedCount} catégorie{ungroupedCount > 1 ? 's' : ''} sans groupe —
            elles apparaissent dans le grand livre, mais sans total d’ensemble.
          </p>
        </div>
      )}

      <Panel title={`Catégories${categories.length ? ` · ${categories.length}` : ''}`}>
        {loading ? (
          <Loading label="Chargement des catégories…" />
        ) : categories.length === 0 ? (
          <EmptyState
            title="Aucune catégorie"
            hint="Ajoutez-en une ci-dessus pour commencer à classer vos écritures."
          />
        ) : (
          <div className="flex flex-col gap-[18px]">
            {grouped.map(([group, items]) => (
              <div key={group}>
                <div className="flex items-baseline gap-2 mb-2">
                  <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">
                    {group === UNGROUPED ? 'Sans groupe' : group}
                  </p>
                  <span className="text-[10.5px] text-[#5C554C] tabular-nums">{items.length}</span>
                </div>

                <div className="flex flex-col gap-1.5">
                  {items.map((cat) => (
                    <div key={cat._id}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-[#241F1B] bg-[#1B1815]">
                      <span className="text-[13px] text-[#F4F1EA] flex-1 truncate">{cat.name}</span>

                      {editing?.id === cat._id ? (
                        <>
                          <input
                            autoFocus
                            value={editing.group}
                            onChange={(e) => setEditing({ ...editing, group: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') assignGroup(cat._id, editing.group);
                              if (e.key === 'Escape') setEditing(null);
                            }}
                            placeholder="Nom du groupe"
                            list="known-groups"
                            className={`${inputClass} h-[30px] w-[170px] text-[12px]`}
                          />
                          <Button as="button" type="button" variant="primary"
                            className="h-[30px]"
                            onClick={() => assignGroup(cat._id, editing.group)}
                            disabled={busyId === cat._id}>
                            {busyId === cat._id ? '…' : 'Enregistrer'}
                          </Button>
                          <Button as="button" type="button" className="h-[30px]"
                            onClick={() => setEditing(null)}>Annuler</Button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditing({ id: cat._id, group: cat.group || '' })}
                          className="text-[11.5px] text-[#8F877B] hover:text-[#F4F1EA] transition-colors"
                        >
                          {cat.group ? 'Changer de groupe' : 'Assigner un groupe'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </Page>
  );
};

export default CategoryManager;
